<template>
  <IndicatorCard
    :loading="loading"
    :has-data="orderTypes.length > 0"
    :is-result="isResult"
    :no-data-message="$t('Nenhum dado de tempo produtivo encontrado')"
    :title="title"
    :description="indicator.description"
    :excel-endpoint="indicator.excelEndpoint"
    :hide-excel="!isResult"
    :excel-loading="excelLoading"
    @export-excel="requestExcel"
  >
    <template #date-range>
      <DateRangePicker
        :start-date="startDate"
        :end-date="endDate"
        :disabled="isResult"
        @change="onDateRangeChange"
      />
    </template>

    <v-row justify="center">
      <v-col cols="12" lg="10">
        <BaseEChart :option="chartOption" :loading="loading" height="320px" />
      </v-col>

      <v-col cols="12" v-for="orderType in orderTypes" :key="orderType.serviceOrderType">
        <div class="d-flex align-center flex-wrap mt-3">
          <span class="subtitle">{{ orderType.serviceOrderTypeName }}</span>
          <v-chip class="ml-2" color="#BBCF20" text-color="#fff" small>
            {{ orderType.quantityStep }} {{ $t('etapas') }} ·
            {{ $t('Média') }} {{ orderType.averageTime }} ·
            {{ orderType.totalTime }}
          </v-chip>
        </div>

        <v-data-table
          :loading="loading"
          :loading-text="$t('Carregando...')"
          :no-data-text="$t('Nenhum registro encontrado')"
          :headers="headers"
          :items="orderType.users"
          class="main-table mt-2"
          dense
          hide-default-footer
          :items-per-page="-1"
          :mobile-breakpoint="0"
        />
      </v-col>
    </v-row>

    <template #review>
      <v-row class="ma-0">
        <v-col cols="12" v-if="isResult && review">
          <strong>{{ $t('Análise crítica') }}</strong>
          <p v-html="review"></p>
        </v-col>
        <v-col cols="12" v-else-if="!isResult">
          <tiptap-vuetify
            v-model="filter.review"
            :extensions="extensions"
            :label="$t('Análise crítica')"
            :placeholder="
              $t('Digite sua análise crítica para o indicador abaixo')
            "
            :card-props="{ flat: true, outlined: true, rounded: true }"
            class="primary-input"
            outlined
            dense
            hide-details="auto"
          />
        </v-col>
      </v-row>
    </template>
  </IndicatorCard>
</template>

<script>
import { getIndicatorById } from '../../../../../indicators/indicatorRegistry'
import ApiService from '../../../../../../../../services/ApiService'
import IndicatorCard from '../../shared/IndicatorCard.vue'
import BaseEChart from '../../shared/BaseEChart.vue'
import DateRangePicker from '../../shared/DateRangePicker.vue'
import { buildBarOption } from '../../shared/chartConfigs'
import moment from 'moment'
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from 'tiptap-vuetify'

export default {
  components: {
    IndicatorCard,
    BaseEChart,
    DateRangePicker,
    TiptapVuetify,
  },

  props: {
    title: { type: String, default: '' },
    apiEndpointOverride: { type: String, default: '' },
    filterProp: Object,
    institution: Object,
    unidades: Array,
    review: String,
    isResult: { type: Boolean, default: false },
  },

  async created() {
    this.filter.institutionId = this.institution.id
    this.filter.unidadeIds = this.unidades

    const lastMonthStart = moment()
      .add(-1, 'month')
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD')
    const lastMonthEnd = moment()
      .add(-1, 'month')
      .clone()
      .endOf('month')
      .format('YYYY-MM-DD')

    this.filter.startDate = lastMonthStart
    this.filter.endDate = lastMonthEnd
    this.startDate = lastMonthStart
    this.endDate = lastMonthEnd

    if (this.filterProp) {
      this.filter = this.filterProp
      this.startDate = moment(this.filter.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.endDate = moment(this.filter.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    }

    this.list()
  },

  data: () => ({
    excelLoading: false,
    extensions: [
      History, Blockquote, Link, Underline, Strike, Italic, ListItem,
      BulletList, OrderedList,
      [Heading, { options: { levels: [1, 2, 3] } }],
      Bold, Code, HorizontalRule, Paragraph, HardBreak,
    ],
    loading: true,
    orderTypes: [],
    filter: {
      institutionId: null,
      unidadeIds: [],
      startDate: null,
      endDate: null,
      review: '',
    },
    startDate: null,
    endDate: null,
    apiService: new ApiService(),
  }),

  computed: {
    indicator() {
      return getIndicatorById('productive_time_order_type') || {}
    },

    headers() {
      return [
        { text: this.$t('Técnico'), value: 'userName', align: 'center' },
        { text: this.$t('Qtd Etapas'), value: 'quantityStep', align: 'center' },
        { text: this.$t('Tempo Médio'), value: 'averageTime', align: 'center' },
        { text: this.$t('Tempo Total'), value: 'totalTime', align: 'center' },
      ]
    },

    chartOption() {
      return buildBarOption({
        categories: this.orderTypes.map((o) => o.serviceOrderTypeName),
        series: [
          {
            name: this.$t('Tempo Total das Etapas (h)'),
            data: this.orderTypes.map((o) => this.timeToHours(o.totalTime)),
            color: '#0F3565',
          },
        ],
        yAxisName: 'Horas',
        showLegend: false,
      })
    },
  },

  methods: {
    /** Converts the "125H07m" backend format into decimal hours. */
    timeToHours(time) {
      if (!time) return 0
      const match = String(time).match(/(\d+)H(\d+)/i)
      if (!match) return 0
      return +(Number(match[1]) + Number(match[2]) / 60).toFixed(2)
    },

    requestExcel() {
      if (!this.indicator.excelEndpoint) return
      this.excelLoading = true
      const filename = `${this.indicator.legacyKey}.xlsx`
      this.apiService
        .post(this.indicator.excelEndpoint, { ...this.filter, pageSize: 999999 })
        .then((response) => {
          const data = `data:application/octet-stream;base64,${response.content}`
          const link = document.createElement('a')
          link.href = data
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .finally(() => {
          this.excelLoading = false
        })
    },

    setDates() {
      this.filter.startDate = moment(this.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.filter.endDate = moment(this.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    },

    onDateRangeChange({ startDate, endDate }) {
      this.startDate = startDate
      this.endDate = endDate
      if (this.filterProp) this.filterProp.data = null
      this.list()
    },

    setInfos() {
      const model = {
        chart: getIndicatorById('productive_time_order_type').legacyKey,
        ...this.filter,
        data: null,
      }
      this.$emit('setInfos', model)
    },

    extractChartData(response) {
      this.orderTypes = Array.isArray(response?.content) ? response.content : []
    },

    async list() {
      this.loading = true
      try {
        if (this.filterProp.data) {
          this.extractChartData(JSON.parse(this.filterProp.data))
          return
        }

        if (!this.isResult) this.setDates()
        this.filter.institutionId = this.institution.id
        this.filter.unidadeIds = this.unidades

        const response = await this.apiService.post(
          this.apiEndpointOverride || 'reports/productiveTimeByOrderType',
          this.filter
        )
        this.extractChartData(response)
      } catch (e) {
        this.orderTypes = []
      } finally {
        this.loading = false
      }
    },
  },

  watch: {
    filter: {
      deep: true,
      handler() {
        this.setInfos()
      },
    },
  },
}
</script>
